import useDiscountExclusivity from './useDiscountExclusivity';

/**
 * The surface asking whether to show the grey general exclusivity note.
 *  - `offer`         → the offer detail sheet (OfferModal)
 *  - `promo_or_gift` → the promo/gift entry modals (AddPromoOrGiftCardModal, MultiPromoCodeModal)
 */
export type ExclusivityNoteSurface = 'offer' | 'promo_or_gift';

/**
 * Should the grey general exclusivity note be shown on this surface?
 *
 * UX point 7 (PROD-407): when the customer has applied nothing and there is no conflict, the note
 * is just noise — don't show it at all. So the note only appears when applying the thing on THIS
 * surface would actually displace an active discount:
 *
 *  - `offer`: applying an offer only conflicts with an active promo code or gift card
 *    (`offerBlockedBy`). Offers stack with each other and coexist with points, so those are not
 *    conflicts — with nothing (or only offers/points) applied the note stays hidden.
 *  - `promo_or_gift`: a new promo code or gift card displaces ANY active discount (full mutual
 *    exclusivity), so the note shows whenever something is already applied.
 *
 * State-only; consumes {@link useDiscountExclusivity}. Component-level gating (e.g. the comparison
 * card already replacing the note) is layered on top by each surface.
 */
export default function useShowExclusivityNote(surface: ExclusivityNoteSurface): boolean {
  const { hasOffer, hasPromo, hasGift, hasPoints, offerBlockedBy } = useDiscountExclusivity();

  if (surface === 'offer') {
    return offerBlockedBy !== null;
  }

  return hasOffer || hasPromo || hasGift || hasPoints;
}
